import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import IconPlus from "../icons/iconPlus.tsx";
import IconMinus from "../icons/iconMinus.tsx";
import { usePendingDelay } from "../hooks/usePendingDelay";
import { PendingContent, PendingDots } from "../shared/PendingDots";

type QuantityControlsVariant = "menu" | "basket";

interface QuantityControlsProps {
  /** "menu" shows Add or minus/input/plus; "basket" shows minus/input/plus. */
  variant: QuantityControlsVariant;
  /** Current item quantity in the basket */
  quantity: number;
  /** Display name for accessibility labels */
  itemName?: string;
  /** Whether the control is in a loading state */
  isLoading?: boolean;
  /** Whether the add/plus button should be disabled (e.g. the item contains a guest's allergen) */
  disabled?: boolean;
  /** Id of the element explaining why the add/plus button is disabled */
  describedBy?: string;
  /** Handler for the add/plus action */
  onIncrease: () => void;
  /** Handler for the minus action when quantity is greater than zero. */
  onDecrease?: () => void;
  /**
   * Committed when the user finishes editing the quantity input (on blur or Enter).
   * Receives the typed absolute quantity, or `null` when the field was left empty.
   */
  onQuantityCommit?: (value: number | null) => void;
}

/**
 * Unified quantity controls used in both the menu and basket views.
 *
 * - **menu** variant: shows Add at zero, or an editable quantity with
 *   minus and plus controls when the item is in the basket.
 * - **basket** variant: shows minus / quantity input / plus controls.
 *
 * While `isLoading` (this item's update is in flight) the whole group is
 * disabled with `aria-busy`; after a short pending delay the content is
 * visually replaced by the pending dots until the update settles.
 */
const QuantityControls: React.FC<QuantityControlsProps> = ({
  variant,
  quantity,
  itemName = "",
  isLoading = false,
  disabled = false,
  describedBy,
  onIncrease,
  onDecrease,
  onQuantityCommit,
}) => {
  const { t } = useTranslation();

  // Hold the raw text while editing so the box can be emptied and typed into freely.
  // When not editing, the field mirrors the stored quantity.
  const [inputValue, setInputValue] = useState<string>(String(quantity));
  const [isEditing, setIsEditing] = useState(false);

  const showDots = usePendingDelay(isLoading);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(String(quantity));
    }
  }, [quantity, isEditing]);

  const handleKeyDown = (action: "increase" | "decrease") => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      if (action === "increase") {
        onIncrease();
      } else {
        onDecrease?.();
      }
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    // Select the existing value so typing replaces it rather than appending.
    e.target.select();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow an empty field (mid-edit) or digits only.
    if (raw === "" || /^\d+$/.test(raw)) {
      setInputValue(raw);
    }
  };

  const commitInput = () => {
    setIsEditing(false);
    const trimmed = inputValue.trim();
    const typedQuantity = trimmed === "" ? null : Number(trimmed);
    if (disabled && typedQuantity !== null && typedQuantity > quantity) {
      setInputValue(String(quantity));
      return;
    }
    onQuantityCommit?.(typedQuantity);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    type: "text",
    inputMode: "numeric",
    value: inputValue,
    onFocus: handleInputFocus,
    onChange: handleInputChange,
    onBlur: commitInput,
    onKeyDown: handleInputKeyDown,
    disabled: isLoading,
    "aria-busy": isLoading || undefined,
  };

  const increaseLabel = itemName
    ? t("increase_amount_for", "Increase amount for {{name}}", { name: itemName })
    : t("Increase amount");
  const decreaseLabel = itemName
    ? t("decrease_amount_for", "Decrease amount for {{name}}", { name: itemName })
    : t("Decrease amount");

  const renderQuantityControls = () => (
    <span className="menu-amount">
      <button
        type="button"
        className={`icon-amount${variant === "menu" ? " icon-minus" : ""}`}
        aria-label={decreaseLabel}
        onClick={() => onDecrease?.()}
        onKeyDown={handleKeyDown("decrease")}
        disabled={isLoading}
        aria-busy={isLoading || undefined}
      >
        <PendingContent showDots={showDots}>
          <IconMinus color={"#fff"} />
        </PendingContent>
      </button>
      <span className="gcw:relative gcw:inline-flex">
        <input
          {...inputProps}
          aria-label={`${t("Amount for")} ${itemName}`}
          className={showDots ? "gcw:text-transparent" : undefined}
        />
        {showDots && <PendingDots className="gcw:absolute gcw:inset-0 gcw:pointer-events-none" />}
      </span>
      <button
        type="button"
        className={`icon-amount${variant === "menu" ? " icon-plus" : ""}`}
        aria-label={increaseLabel}
        onClick={onIncrease}
        onKeyDown={handleKeyDown("increase")}
        disabled={isLoading || disabled}
        aria-busy={isLoading || undefined}
        aria-describedby={describedBy}
      >
        <PendingContent showDots={showDots}>
          <IconPlus color={"#fff"} />
        </PendingContent>
      </button>
    </span>
  );

  return (
    <div className={`gcw:flex gcw:items-center menu-quantity-controls${variant === "basket" ? " gcw:pt-2" : ""}`}>
      {variant === "menu" && quantity <= 0 ? (
        <span className="menu-amount">
          <button
            type="button"
            className="icon-amount add"
            aria-label={increaseLabel}
            onClick={onIncrease}
            onKeyDown={handleKeyDown("increase")}
            disabled={isLoading || disabled}
            aria-busy={isLoading || undefined}
            aria-describedby={describedBy}
          >
            <PendingContent showDots={showDots}>{t("Add")}</PendingContent>
          </button>
        </span>
      ) : (
        renderQuantityControls()
      )}
    </div>
  );
};

export default QuantityControls;
